import { API_BASE_URL, resolvePublicMediaUrl } from '../config/api';
import { parseNewsListMeta } from './cmsNews';

/**
 * @typedef {{
 *   id: string,
 *   title: string,
 *   coverImageUrl: string,
 *   pdfUrl: string,
 *   order: number,
 *   isActive: boolean,
 *   isPublished: boolean,
 *   createdAtUtc: string,
 *   publishedAtUtc?: string,
 * }} CmsNewsletterItem
 */

/**
 * @typedef {{
 *   id: string,
 *   title: string,
 *   createdAt: string,
 *   coverImageSrc: string,
 *   pdfUrl: string,
 * }} Bulletin
 */

/**
 * @param {CmsNewsletterItem} item
 * @returns {Bulletin}
 */
export function mapNewsletterToBulletin(item) {
    const dateSrc = item.publishedAtUtc || item.createdAtUtc || '';
    const datePart =
        typeof dateSrc === 'string' && dateSrc.length >= 10
            ? dateSrc.slice(0, 10)
            : '';
    return {
        id: item.id,
        title: item.title || 'Bülten',
        createdAt: datePart,
        coverImageSrc: resolvePublicMediaUrl(item.coverImageUrl),
        pdfUrl: resolvePublicMediaUrl(item.pdfUrl),
    };
}

/**
 * @param {unknown[]} list
 * @returns {CmsNewsletterItem[]}
 */
function processNewsletterList(list) {
    return /** @type {CmsNewsletterItem[]} */ (list)
        .filter(
            (x) =>
                x &&
                typeof x === 'object' &&
                /** @type {{ isActive?: boolean, isPublished?: boolean }} */ (x)
                    .isActive === true &&
                /** @type {{ isPublished?: boolean }} */ (x).isPublished ===
                    true,
        )
        .sort((a, b) => {
            const ta = new Date(
                a.publishedAtUtc || a.createdAtUtc || 0,
            ).getTime();
            const tb = new Date(
                b.publishedAtUtc || b.createdAtUtc || 0,
            ).getTime();
            return tb - ta;
        });
}

/**
 * @param {{ pageNumber?: number, pageSize?: number }} opts
 */
async function fetchNewslettersGetAllRaw(opts) {
    const pageNumber = opts.pageNumber ?? 1;
    const pageSize = opts.pageSize ?? 10;
    const params = new URLSearchParams({
        pageNumber: String(pageNumber),
        pageSize: String(pageSize),
    });
    const url = `${API_BASE_URL}/cms/Newsletter/GetAll?${params}`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
    });

    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error('Bülten listesi okunamadı');
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `Bülten listesi alınamadı (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : 'Bülten listesi alınamadı';
        throw new Error(msg);
    }

    const raw = /** @type {{ data?: unknown, metaData?: unknown }} */ (obj)
        .data;
    const list = Array.isArray(raw) ? raw : [];
    const metaData =
        /** @type {{ metaData?: unknown }} */ (obj).metaData ?? null;

    return { list, metaData };
}

/**
 * Sayfalı bülten listesi (CMS Newsletter/GetAll).
 * @param {{ pageNumber?: number, pageSize?: number }} [opts]
 * @returns {Promise<{ items: Bulletin[], meta: ReturnType<typeof parseNewsListMeta> }>}
 */
export async function fetchCmsNewslettersPaged(opts = {}) {
    const pageNumber = opts.pageNumber ?? 1;
    const pageSize = opts.pageSize ?? 10;
    const { list, metaData } = await fetchNewslettersGetAllRaw({
        pageNumber,
        pageSize,
    });
    const processed = processNewsletterList(list);
    const meta = parseNewsListMeta(
        metaData,
        processed.length,
        pageNumber,
        pageSize,
    );
    const items = processed
        .map(mapNewsletterToBulletin)
        .filter((x) => x.pdfUrl);
    return { items, meta };
}
